import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { buildStringMatrix } from '../../../utils/export/tableDataTransformer.js';
import { buildCsvBlob } from '../../../utils/export/csvExport.js';
import { buildExportFilename, downloadBlob } from '../../../utils/export/downloadHelpers.js';
import { downloadExcelFromMatrix } from '../../../utils/export/excelExport.js';

/** @typedef {'current-page' | 'filtered' | 'all'} ExportScope */

/**
 * Shared export logic for diagnostics tables.
 *
 * @param {object} params
 * @param {string} [params.exportSlug]
 * @param {(extension: 'csv' | 'xlsx') => string} [params.getExportFilename]
 * @param {string[]} params.columnKeys
 * @param {string[]} params.headerLabels
 * @param {(scope: ExportScope) => Record<string, unknown>[]} params.getExportRows
 * @param {() => { currentPage: number, filtered: number, all: number }} params.getExportScopeCounts
 * @param {string} params.sheetTitle
 * @param {string} [params.emptyDisplay='- -']
 */
export function useTableExport({
  exportSlug,
  getExportFilename,
  columnKeys,
  headerLabels,
  getExportRows,
  getExportScopeCounts,
  sheetTitle,
  emptyDisplay = '- -',
}) {
  const hasFilenameStrategy =
    typeof getExportFilename === 'function' ||
    (typeof exportSlug === 'string' && exportSlug.trim().length > 0);

  const resolveScopeCounts = useCallback(() => {
    if (typeof getExportScopeCounts !== 'function') {
      return { currentPage: 0, filtered: 0, all: 0 };
    }
    const counts = getExportScopeCounts();
    return {
      currentPage: counts?.currentPage ?? 0,
      filtered: counts?.filtered ?? 0,
      all: counts?.all ?? 0,
    };
  }, [getExportScopeCounts]);

  const canExport =
    hasFilenameStrategy &&
    columnKeys.length > 0 &&
    (resolveScopeCounts().currentPage > 0 ||
      resolveScopeCounts().filtered > 0 ||
      resolveScopeCounts().all > 0);

  const resolveFilename = useCallback(
    (ext) => {
      const normalized = ext === 'xlsx' ? 'xlsx' : 'csv';
      if (typeof getExportFilename === 'function') {
        const name = getExportFilename(normalized);
        return String(name || '').trim() || buildExportFilename(exportSlug || 'export', normalized);
      }
      return buildExportFilename(exportSlug || 'export', normalized);
    },
    [getExportFilename, exportSlug]
  );

  const [isExporting, setIsExporting] = useState(false);

  const buildMatrixForScope = useCallback(
    (scope) => {
      const rows = typeof getExportRows === 'function' ? getExportRows(scope) : [];
      return buildStringMatrix(rows, columnKeys, headerLabels, emptyDisplay);
    },
    [getExportRows, columnKeys, headerLabels, emptyDisplay]
  );

  const exportCsv = useCallback(
    async (opts = {}) => {
      const scope = opts.scope ?? 'current-page';
      const counts = resolveScopeCounts();
      const scopeCount =
        scope === 'all' ? counts.all : scope === 'filtered' ? counts.filtered : counts.currentPage;
      if (!hasFilenameStrategy || columnKeys.length === 0 || scopeCount === 0) return;

      setIsExporting(true);
      try {
        await Promise.resolve();
        const matrix = buildMatrixForScope(scope);
        const blob = buildCsvBlob(matrix);
        const forced = opts.filename != null ? String(opts.filename).trim() : '';
        const name = forced || resolveFilename('csv');
        downloadBlob(name, blob);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'CSV export failed';
        toast.error(msg);
      } finally {
        setIsExporting(false);
      }
    },
    [hasFilenameStrategy, columnKeys.length, resolveScopeCounts, buildMatrixForScope, resolveFilename]
  );

  const exportExcel = useCallback(
    async (opts = {}) => {
      const scope = opts.scope ?? 'current-page';
      const counts = resolveScopeCounts();
      const scopeCount =
        scope === 'all' ? counts.all : scope === 'filtered' ? counts.filtered : counts.currentPage;
      if (!hasFilenameStrategy || columnKeys.length === 0 || scopeCount === 0) return;

      setIsExporting(true);
      try {
        await Promise.resolve();
        const matrix = buildMatrixForScope(scope);
        const forced = opts.filename != null ? String(opts.filename).trim() : '';
        const name = forced || resolveFilename('xlsx');
        await downloadExcelFromMatrix({
          matrix,
          sheetTitle,
          filename: name,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Excel export failed';
        toast.error(msg);
      } finally {
        setIsExporting(false);
      }
    },
    [hasFilenameStrategy, columnKeys.length, resolveScopeCounts, buildMatrixForScope, resolveFilename, sheetTitle]
  );

  return {
    exportCsv,
    exportExcel,
    getResolvedExportFilename: resolveFilename,
    getExportScopeCounts: resolveScopeCounts,
    isExporting,
    canExport,
  };
}
